import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import ScrollLock from 'react-scrolllock';
import styled from '@emotion/styled';

import api from 'lib/api';
import LoadingIndicator from 'components/loading-indicator';

/* ------------------------------------------------------------------ *
 * The branch manager's inline editor for an enrollment-year plan.
 *   - Household targets: a target per enrollment type (new/renewal) x category
 *     (indigent/paying) -- a 2x2 grid.
 *   - Member targets: a single number for the scope (no type/category).
 * Saves everything for the scope in one bulk-upsert call.
 * ------------------------------------------------------------------ */

const PLAN_TYPES = ['new', 'renewal'];
const CATEGORIES = ['indigent', 'paying'];

const LABELS = {
  new: 'New',
  renewal: 'Renewal',
  indigent: 'Indigent',
  paying: 'Paying',
};

const householdKey = (planType, category) => `household|${planType}|${category}`;
const MEMBER_KEY = 'member';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(16, 24, 40, 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow-y: auto;
  padding: 48px 16px;
  z-index: 9000;
`;

const Card = styled.div`
  width: 100%;
  max-width: 620px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 24px 48px rgba(16, 24, 40, 0.24);
  overflow: hidden;
`;

const CardHead = styled.div`
  padding: 22px 26px 18px;
  border-bottom: 1px solid #eef0f4;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 19px;
  font-weight: 700;
  color: #101828;
`;

const Subtitle = styled.div`
  margin-top: 6px;
  font-size: 13px;
  color: #667085;
`;

const Body = styled.div`
  padding: 20px 26px;
`;

const Group = styled.section`
  border: 1px solid #e7eaf0;
  border-radius: 12px;
  padding: 16px 18px;
  margin-bottom: 18px;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const GroupTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #475467;
  margin-bottom: 14px;
`;

const Matrix = styled.div`
  display: grid;
  grid-template-columns: 110px 1fr 1fr;
  gap: 10px 14px;
  align-items: center;
`;

const ColHead = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #98a2b3;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  text-align: center;
`;

const RowHead = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #344054;
`;

const MemberRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
`;

const MemberHint = styled.div`
  font-size: 13px;
  color: #667085;
`;

const NumberField = styled.input`
  width: 100%;
  height: 38px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 15px;
  color: #101828;
  text-align: right;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    outline: none;
    border-color: #3b6cf6;
    box-shadow: 0 0 0 3px rgba(59, 108, 246, 0.16);
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

const MemberField = styled(NumberField)`
  max-width: 180px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 18px 26px;
  border-top: 1px solid #eef0f4;
  background: #fbfbfd;
`;

const Btn = styled.button`
  appearance: none;
  border-radius: 8px;
  padding: 0 18px;
  height: 40px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelBtn = styled(Btn)`
  background: #fff;
  border-color: #d0d5dd;
  color: #344054;

  &:hover:not(:disabled) {
    border-color: #98a2b3;
  }
`;

const SaveBtn = styled(Btn)`
  background: #3b6cf6;
  color: #fff;

  &:hover:not(:disabled) {
    background: #2f5bd8;
  }
`;

const ErrorText = styled.div`
  color: #b42318;
  font-size: 13px;
  margin-right: auto;
`;

const StateWrap = styled.div`
  padding: 32px 0;
  text-align: center;
  color: #667085;
`;

function blankTargets() {
  const targets = { [MEMBER_KEY]: '' };
  PLAN_TYPES.forEach((planType) => {
    CATEGORIES.forEach((category) => {
      targets[householdKey(planType, category)] = '';
    });
  });
  return targets;
}

function BranchPlanEditor({ scope, onClose, onSaved }) {
  const [targets, setTargets] = useState(blankTargets);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError('');

    api.fetchEnrollmentPlans({
      enrollmentYearId: scope.enrollmentYearId,
      branchId: scope.branchId,
      administrativeDivisionId: scope.administrativeDivisionId,
    })
      .then((plans) => {
        if (cancelled) return;
        const next = blankTargets();
        (plans || []).forEach((plan) => {
          if (plan.beneficiaryType === 'member') {
            next[MEMBER_KEY] = String(plan.targetCount);
          } else {
            const key = householdKey(plan.planType, plan.category);
            if (key in next) next[key] = String(plan.targetCount);
          }
        });
        setTargets(next);
      })
      .catch((err) => {
        if (!cancelled) setError(readError(err) || 'Failed to load existing targets');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [scope.enrollmentYearId, scope.branchId, scope.administrativeDivisionId]);

  const handleChange = key => (event) => {
    const { value } = event.target;
    if (value !== '' && !/^\d+$/.test(value)) return; // non-negative integers only
    setTargets(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    setIsSaving(true);
    setError('');

    const plans = [{
      beneficiaryType: 'member',
      targetCount: parseInt(targets[MEMBER_KEY], 10) || 0,
    }];
    PLAN_TYPES.forEach((planType) => {
      CATEGORIES.forEach((category) => {
        plans.push({
          planType,
          beneficiaryType: 'household',
          category,
          targetCount: parseInt(targets[householdKey(planType, category)], 10) || 0,
        });
      });
    });

    api.saveEnrollmentPlans({
      enrollmentYearId: scope.enrollmentYearId,
      branchId: scope.branchId,
      administrativeDivisionId: scope.administrativeDivisionId,
      plans,
    })
      .then(() => {
        onSaved();
        onClose();
      })
      .catch((err) => {
        setError(readError(err) || 'Failed to save targets');
        setIsSaving(false);
      });
  };

  const scopeLabel = scope.administrativeDivisionName
    ? `${scope.branchName} › ${scope.administrativeDivisionName}`
    : `${scope.branchName} (whole branch)`;

  return ReactDOM.createPortal(
    <Overlay onMouseDown={(e) => { if (e.target === e.currentTarget && !isSaving) onClose(); }}>
      <ScrollLock>
        <Card role="dialog" aria-modal="true" aria-label="Edit enrollment plan targets">
          <CardHead>
            <Title>Enrollment plan targets</Title>
            <Subtitle>{`${scopeLabel} • Enrollment year ${scope.enrollmentYear}`}</Subtitle>
          </CardHead>

          <Body>
            {isLoading ? (
              <StateWrap><LoadingIndicator noun="targets" /></StateWrap>
            ) : (
              <>
                <Group>
                  <GroupTitle>Households</GroupTitle>
                  <Matrix>
                    <div />
                    {PLAN_TYPES.map(planType => (
                      <ColHead key={planType}>{LABELS[planType]}</ColHead>
                    ))}
                    {CATEGORIES.map(category => (
                      <React.Fragment key={category}>
                        <RowHead>{LABELS[category]}</RowHead>
                        {PLAN_TYPES.map(planType => (
                          <NumberField
                            key={planType}
                            type="number"
                            min="0"
                            inputMode="numeric"
                            placeholder="0"
                            disabled={isSaving}
                            value={targets[householdKey(planType, category)]}
                            onChange={handleChange(householdKey(planType, category))}
                            aria-label={`Household ${LABELS[planType]} ${LABELS[category]} target`}
                          />
                        ))}
                      </React.Fragment>
                    ))}
                  </Matrix>
                </Group>

                <Group>
                  <GroupTitle>Members</GroupTitle>
                  <MemberRow>
                    <MemberHint>Total member enrollment target for this scope</MemberHint>
                    <MemberField
                      type="number"
                      min="0"
                      inputMode="numeric"
                      placeholder="0"
                      disabled={isSaving}
                      value={targets[MEMBER_KEY]}
                      onChange={handleChange(MEMBER_KEY)}
                      aria-label="Member target"
                    />
                  </MemberRow>
                </Group>
              </>
            )}
          </Body>

          <Footer>
            {error ? <ErrorText>{error}</ErrorText> : null}
            <CancelBtn type="button" onClick={onClose} disabled={isSaving}>Cancel</CancelBtn>
            <SaveBtn type="button" onClick={handleSave} disabled={isLoading || isSaving}>
              {isSaving ? 'Saving…' : 'Save targets'}
            </SaveBtn>
          </Footer>
        </Card>
      </ScrollLock>
    </Overlay>,
    document.body,
  );
}

function readError(err) {
  return err
    && err.response
    && err.response.data
    && err.response.data.errors
    && err.response.data.errors.message;
}

BranchPlanEditor.propTypes = {
  scope: PropTypes.shape({
    enrollmentYearId: PropTypes.number,
    enrollmentYear: PropTypes.number,
    branchId: PropTypes.number,
    branchName: PropTypes.string,
    administrativeDivisionId: PropTypes.number,
    administrativeDivisionName: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default BranchPlanEditor;
